import { VenueStore } from "./cache";
import { renderToastMenus, showToastLoading } from "./toast";

// --- API configuration ---------------------------------------------------
// Split out so the endpoint is a one-line change if it ever moves.
const API_BASE = "https://app.tablevoiceqa.com";
const API_VERSION = "v1";

/** Builds the public venue endpoint for a given venue ID. */
function venueUrl(venueId: string): string {
  return `${API_BASE}/api/${API_VERSION}/public/venue/${encodeURIComponent(venueId)}`;
}

/** Abort a venue fetch that hasn't responded within 10s, so a stalled
 * connection can't leave a request (and its dedup entry) hanging forever. */
const FETCH_TIMEOUT = 10 * 1000;

// --- Cache configuration --------------------------------------------------
/** Default time a cached venue is considered fresh: 1 hour. */
const DEFAULT_TTL = 60 * 60 * 1000;

/** Hard cap on serving stale data: entries older than this (or older than the
 * configured TTL, whichever is larger) are evicted and refetched up front
 * rather than served stale. */
const MAX_STALE_AGE = 24 * 60 * 60 * 1000;

// --- Types ----------------------------------------------------------------
// Custom options are wired up later; kept open for now.
export interface TableVoiceOptions {
  [key: string]: unknown;
}

export interface TableVoiceCacheConfig {
  /** How long a cached venue stays fresh, in ms. Defaults to 1 hour. */
  ttl?: number;
  /** Set true to bypass the cache entirely and always fetch fresh. */
  disabled?: boolean;
}

export interface TableVoiceConfig {
  options?: TableVoiceOptions;
  cache?: TableVoiceCacheConfig;
  /** When true, render this venue's Toast menu titles into the page by cloning
   * the authored `[data-menu="menu-title"]` element once per menu. */
  renderToast?: boolean;
}

// Venue payload shape is not modelled yet.
export type VenueData = Record<string, unknown>;

// --- Shared cache + request dedup ----------------------------------------
// Module-level so every TableVoiceClient shares one cache and dedups
// concurrent fetches for the same venue.
const store = new VenueStore<VenueData>();
const inflight = new Map<string, Promise<VenueData>>();

/** Fetches a venue's public data from the API. Throws on a non-2xx response. */
async function fetchVenue(venueId: string): Promise<VenueData> {
  const res = await fetch(venueUrl(venueId), {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(FETCH_TIMEOUT),
  });

  if (!res.ok) {
    throw new Error(
      `TableVoice: failed to fetch venue "${venueId}" (${res.status} ${res.statusText})`,
    );
  }

  return (await res.json()) as VenueData;
}

/**
 * Fetches a venue, deduping concurrent calls for the same ID so only one
 * network request is ever in flight per venue. Only successful responses are
 * cached; pass persist=false to fetch without writing to the cache.
 */
function revalidate(venueId: string, persist = true): Promise<VenueData> {
  const existing = inflight.get(venueId);
  if (existing) return existing;

  const promise = fetchVenue(venueId)
    .then((data) => {
      if (persist) store.set(venueId, data);
      return data;
    })
    .finally(() => {
      inflight.delete(venueId);
    });

  inflight.set(venueId, promise);
  return promise;
}

// --- Client ---------------------------------------------------------------
class TableVoiceClient {
  readonly venueId: string;
  readonly config: TableVoiceConfig;
  readonly cacheTtl: number;
  readonly renderToast: boolean;
  venue: VenueData | null = null;

  constructor(venueId: string, config: TableVoiceConfig = {}) {
    this.venueId = venueId;
    this.config = config;
    this.cacheTtl = resolveTtl(config.cache);
    this.renderToast = config.renderToast === true;
  }

  /**
   * Resolves this venue's data, using a stale-while-revalidate cache:
   *  - fresh cache hit  → returned immediately, no network
   *  - stale cache hit  → returned immediately, refreshed in the background
   *  - miss             → fetched, cached, returned
   * With caching disabled it always fetches fresh.
   */
  async load(): Promise<VenueData> {
    const cached = this.cacheTtl > 0 ? store.get(this.venueId) : null;

    if (cached) {
      const age = Date.now() - cached.storedAt;

      // Too old to serve at all — evict and fall through to a blocking fetch.
      if (age >= Math.max(this.cacheTtl, MAX_STALE_AGE)) {
        store.delete(this.venueId);
      } else {
        this.venue = cached.data;
        this.renderIfEnabled();

        if (age >= this.cacheTtl) {
          // Stale — serve now, refresh in the background.
          revalidate(this.venueId)
            .then((data) => {
              this.venue = data;
              this.renderIfEnabled();
            })
            .catch((err) => console.error(err));
        }
        return cached.data;
      }
    }

    // Miss (or caching disabled) — fetch, cache, return. Still goes through
    // revalidate so concurrent calls share one request; with caching disabled
    // the result is not written to the cache. No cached data to show, so put up
    // the loading state while the blocking fetch is in flight.
    this.showLoadingIfEnabled();
    this.venue = await revalidate(this.venueId, this.cacheTtl > 0);
    this.renderIfEnabled();
    return this.venue;
  }

  /** Shows the Toast loading placeholder (once the DOM is ready) while a
   * blocking fetch is in flight, keeping the container's contents hidden until
   * {@link renderIfEnabled} renders the menus. No-op unless `renderToast` is
   * enabled; only used on the miss path, since cache hits render immediately. */
  private showLoadingIfEnabled(): void {
    if (!this.renderToast) return;
    whenDomReady(() => showToastLoading());
  }

  /** Renders Toast menu data into the page when the `renderToast` option is on.
   * Deferred until the DOM is ready so it works even when the script loads in
   * <head>; the underlying render is idempotent, so calling it on both the
   * cached read and the background refresh is safe. */
  private renderIfEnabled(): void {
    if (!this.renderToast || !this.venue) return;
    const venue = this.venue;
    whenDomReady(() => renderToastMenus(venue));
  }
}

/** Runs a callback once the DOM is ready to be queried/mutated. Runs
 * synchronously if the document has already finished parsing. */
function whenDomReady(fn: () => void): void {
  if (typeof document === "undefined") return;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  } else {
    fn();
  }
}

/** Resolves the effective TTL from cache config. Returns 0 when disabled;
 * non-finite or negative ttl values fall back to the default. */
function resolveTtl(cache?: TableVoiceCacheConfig): number {
  if (cache?.disabled) return 0;
  const ttl = cache?.ttl;
  if (typeof ttl === "number" && Number.isFinite(ttl) && ttl >= 0) return ttl;
  return DEFAULT_TTL;
}

// --- Entry point ----------------------------------------------------------
/**
 * Initialise TableVoice for a venue and kick off the data fetch.
 *
 *   TableVoice("fa6f560d-763c-4991-9e87-aa9d4975dee2");
 *   TableVoice("fa6f560d-…", { cache: { ttl: 5 * 60 * 1000 } }); // 5 min
 *   TableVoice("fa6f560d-…", { cache: { disabled: true } });     // no cache
 *   TableVoice("fa6f560d-…", { renderToast: true });             // render menus
 */
function TableVoice(
  venueId: string,
  config: TableVoiceConfig = {},
): TableVoiceClient {
  if (!venueId) {
    throw new Error(
      "TableVoice: a venue ID is required, e.g. TableVoice('your-venue-id').",
    );
  }

  const client = new TableVoiceClient(venueId, config);
  client.load().catch((err) => console.error(err));
  return client;
}

declare global {
  interface Window {
    TableVoice: typeof TableVoice;
  }
}

if (typeof window !== "undefined") {
  window.TableVoice = TableVoice;
}
